import "./App.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "primereact/resources/themes/lara-light-amber/theme.css";
import Layout from "components/layout/Layout";
import LoginScreen from "screens/Login";
import { Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "components/Auth/ProtectedRoute/ProtectedRoute ";
import VendorScreen from "screens/Vendor";
import CategoriesScreen from "screens/Categories";
import Sliders from "components/Sliders/Sliders";
import Regions from "components/Regions/Regions";
import Users from "components/Users/Users";
import DriverShifts from "components/DriverShifts/DriverShifts";
import Products from "components/Products/Products";
import AddProduct from "components/Products/AddProduct/AddProduct";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import EditProduct from "components/Products/EditProduct/EditProduct";
import Orders from "components/Order/Order";
import OrderDetails from "components/Order/OrderDetails/OrderDetails";
import Home from "components/HomePage/HomePage";
import MessagingHealth from "components/HomePage/MessagingHealth/MessagingHealth";
import sound from "assets/notification.mp3";
import sound2 from "assets/sound2.wav";
import AddVendorPage from "components/Vendors/AddVendorPage/AddVendorPage";
import EditVendor from "components/Vendors/EditVendor/EditVendor";
import Complains from "components/Complains/Complains";
import VendorCategories from "components/VendorCategories/VendorCategories";
import Cities from "components/Cities/Cities";
import DeliveryOffers from "components/DeliveryOffers/DeliveryOffers";
import Banners from "components/Banners/Banners";
import { useSelector } from "react-redux";
import Setting from "components/Settings/Settings";
import SendNotification from "components/SendNotification/SendNotification";
import NotificationCampaignDetails from "components/SendNotification/NotificationCampaignDetails";
import Options from "components/Options/Options";
import { io } from "socket.io-client";
import { selectAuth } from "store/authSlice";
import DeliveryOrders from "components/DeliveryOrders/Order";
import DeliveryOrderDetails from "components/DeliveryOrders/OrderDetails/OrderDetails";
import Statements from "components/Statements/Statements";
import StatementDetail from "components/Statements/StatementDetail/StatementDetail";
import ElevationGate from "components/Statements/ElevationGate/ElevationGate";
import Reviews from "components/Reviews/Reviews";
import ReviewDetail from "components/Reviews/ReviewDetail/ReviewDetail";
import MyVendorReviews from "components/Reviews/MyVendorReviews";
import MyVendorWallet from "components/Vendors/VendorWallet/MyVendorWallet";
import Groups from "components/groups/Groups";
import VendorTimes from "components/VendorTimes/VendorTimes";
import MenuExtraction from "components/MenuExtraction/MenuExtraction";
import ProductVideos from "components/ProductVideos/ProductVideos";
import Receipt from "components/Order/Receipt";
import { useReactToPrint } from "react-to-print";
import { getOrder, getVendorOrder } from "services/order";
import AutoPrint from "components/AutoPrint/AutoPrint";
import { AUTO_PRINT_STORAGE_KEY } from "constants/autoPrint";
import ErrorBoundary from "components/Ui/ErrorBoundary/ErrorBoundary";
import PageErrorFallback from "components/Ui/ErrorBoundary/PageErrorFallback";
import { canManageReviews } from "utils/permissions";

function App() {
    const [newOrder, setNewOrder] = useState(1);
    const [printQueue, setPrintQueue] = useState([]);
    const [activePrintOrder, setActivePrintOrder] = useState(null);
    const [isPreparingPrintOrder, setIsPreparingPrintOrder] = useState(false);
    const toast = useRef(null);
    const printReceiptRef = useRef(null);
    const adminInfo = useSelector((state) => state.auth.userData);
    const { isAuthenticated, hasHydrated } = useSelector(selectAuth);
    const isVendorDashboard = adminInfo?.role === "vendor";
    const isAutoPrintConfigured = useCallback(() => {
        return localStorage.getItem(AUTO_PRINT_STORAGE_KEY) === "true";
    }, []);

    const showMessage = useCallback((type, head, content, options = {}) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 10000,
            ...options,
        });
    }, []);

    const clearActivePrintOrder = useCallback(() => {
        setPrintQueue((prevQueue) => prevQueue.slice(1));
        setActivePrintOrder(null);
    }, []);

    const isAutoPrintableSocketOrder = useCallback((order) => {
        return Boolean(
            order?.id &&
            order?.status === "not started" &&
            order?.createdAt &&
            order?.updatedAt &&
            order.createdAt === order.updatedAt,
        );
    }, []);

    const loadQueuedOrderForPrint = useCallback(
        async (orderId) => {
            setIsPreparingPrintOrder(true);

            try {
                const response = isVendorDashboard
                    ? await getVendorOrder(orderId, adminInfo?.id)
                    : await getOrder(orderId);
                setActivePrintOrder(response.order);
            } catch {
                showMessage("error", "فشل الطباعة", `تعذر تجهيز طباعة الطلب #${orderId}`);
                setPrintQueue((prevQueue) => prevQueue.slice(1));
            } finally {
                setIsPreparingPrintOrder(false);
            }
        },
        [adminInfo?.id, isVendorDashboard, showMessage],
    );

    const handleAutoPrint = useReactToPrint({
        content: () => printReceiptRef.current,
        onAfterPrint: () => {
            clearActivePrintOrder();
        },
    });

    useEffect(() => {
        if (
            !isVendorDashboard ||
            !isAutoPrintConfigured() ||
            activePrintOrder ||
            isPreparingPrintOrder ||
            printQueue.length === 0
        ) {
            return;
        }

        loadQueuedOrderForPrint(printQueue[0]);
    }, [
        activePrintOrder,
        isPreparingPrintOrder,
        isAutoPrintConfigured,
        isVendorDashboard,
        loadQueuedOrderForPrint,
        printQueue,
    ]);

    useEffect(() => {
        if (!isVendorDashboard || !activePrintOrder) {
            return;
        }

        if (!isAutoPrintConfigured()) {
            clearActivePrintOrder();
            return;
        }

        const timeoutId = window.setTimeout(() => {
            try {
                Promise.resolve(handleAutoPrint()).catch(() => {
                    showMessage(
                        "error",
                        "فشل الطباعة",
                        `تعذر بدء طباعة الطلب #${activePrintOrder.id}`,
                    );
                    clearActivePrintOrder();
                });
            } catch {
                showMessage("error", "فشل الطباعة", `تعذر بدء طباعة الطلب #${activePrintOrder.id}`);
                clearActivePrintOrder();
            }
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        activePrintOrder,
        clearActivePrintOrder,
        handleAutoPrint,
        isAutoPrintConfigured,
        isVendorDashboard,
        showMessage,
    ]);

    useEffect(() => {
        if (!hasHydrated || !isAuthenticated || !adminInfo?.id) return;

        const socket = io(process.env.REACT_APP_API_URL, {
            transports: ["websocket"],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
        });

        const joinRooms = () => {
            if (adminInfo?.role === "vendor") {
                socket.emit("join-room", `vendor_${adminInfo?.id}`);
            } else if (adminInfo?.super_admin) {
                socket.emit("join-room", "admins");
            } else {
                socket.emit("join-room", `city_${adminInfo.cityId}`);
            }
        };

        socket.on("connect", joinRooms);

        socket.on("new-order-admin", (order) => {
            setNewOrder((pre) => pre + 1);
            const audio = new Audio(sound);
            const isNewOrder = isAutoPrintableSocketOrder(order);

            if (isVendorDashboard && isNewOrder && isAutoPrintConfigured()) {
                setPrintQueue((prevQueue) => [...prevQueue, order.id]);
            }

            showMessage(
                "success",
                isNewOrder ? "طلب جديد" : "تم تحديث الطلب",
                isNewOrder
                    ? `تم إضافة طلب جديد برقم #${order.id}`
                    : `تم تحديث الطلب رقم #${order.id}`,
                isNewOrder ? { className: "toast-new-order" } : undefined,
            );

            audio.play().catch((error) => {
                console.error("Error playing notification sound:", error);
            });
        });

        // Fired for every manually entered order — a vendor requesting a driver,
        // a dispatch account logging "go fetch this", an operator transcribing a
        // call, and now a customer's "طلب خاص". The old copy said the order came
        // "من مطعم", which stopped being true once customers could file one.
        // It also had a typo ("اشار") and named no order, despite the payload
        // carrying the whole row — so it is now read, like new-order-admin does.
        socket.on("new-notification-admin", (order) => {
            const audio = new Audio(sound2);
            setNewOrder((pre) => pre + 1);

            showMessage(
                "warn",
                "إشعار جديد",
                order?.id ? `هناك طلب توصيل جديد برقم #${order.id}` : "هناك طلب توصيل جديد",
            );

            audio.play().catch((error) => {
                console.error("Error playing notification sound:", error);
            });
        });

        socket.on("update-order-admin", () => {
            setNewOrder((pre) => pre + 1);
        });

        socket.on("disconnect", (reason) => {
            console.log("Disconnected:", reason);
        });

        return () => {
            socket.removeAllListeners();
            socket.disconnect();
        };
    }, [
        adminInfo,
        hasHydrated,
        isAuthenticated,
        isAutoPrintConfigured,
        isAutoPrintableSocketOrder,
        isVendorDashboard,
        showMessage,
    ]);

    return (
        <>
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            {isVendorDashboard && activePrintOrder ? (
                <div className="auto-print-receipt-host" ref={printReceiptRef}>
                    <Receipt order={activePrintOrder} className="" />
                </div>
            ) : null}
            {/* App-level last resort: catches crashes outside the per-page
                boundary in Layout (sidebar, navbar, login) so the screen
                never goes fully blank; the Toast above stays alive */}
            <ErrorBoundary fallback={<PageErrorFallback isFullScreen />}>
                <Routes>
                    {/* Public Route */}
                    <Route path="/login" element={<LoginScreen />} />

                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Layout newOrder={newOrder} />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Home />} />

                        <Route
                            path="orders/*"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_orders
                                    }
                                >
                                    <Orders newOrder={newOrder} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="orders/:id"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_orders
                                    }
                                >
                                    <OrderDetails showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="vendors"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_vendors
                                    }
                                >
                                    <VendorScreen />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="vendors/:id"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_vendors
                                    }
                                >
                                    <EditVendor showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="vendors/add"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_vendors
                                    }
                                >
                                    <AddVendorPage showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="products"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_products
                                    }
                                >
                                    <Products />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="times"
                            element={
                                <ProtectedRoute condition={adminInfo.role === "vendor"}>
                                    <VendorTimes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="auto-print"
                            element={
                                <ProtectedRoute condition={adminInfo.role === "vendor"}>
                                    <AutoPrint />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="products/:id"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_products
                                    }
                                >
                                    <EditProduct showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="products/add"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_products
                                    }
                                >
                                    <AddProduct showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="menu-extraction"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_vendors
                                    }
                                >
                                    <MenuExtraction showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="product-videos"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.role === "vendor" ||
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_product_videos
                                    }
                                >
                                    <ProductVideos />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="users" element={<Navigate to="/users/customers" replace />} />

                        <Route
                            path="users/customers"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_users
                                    }
                                >
                                    <Users role="customer" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="users/deliveries"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_users
                                    }
                                >
                                    <Users role="delivery" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="driver-shifts"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_orders
                                    }
                                >
                                    <DriverShifts />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="users/admins"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_users
                                    }
                                >
                                    <Users role="admin" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="categories"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_categories
                                    }
                                >
                                    <CategoriesScreen />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="vendor-categories"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_categories
                                    }
                                >
                                    <VendorCategories />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="cities"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin || adminInfo?.roles?.manage_cities
                                    }
                                >
                                    <Cities />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="delivery-offers"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_delivery_offers
                                    }
                                >
                                    <DeliveryOffers />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="complains"
                            element={
                                <ProtectedRoute>
                                    <Complains />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="deliveryOrders"
                            element={
                                <ProtectedRoute>
                                    <DeliveryOrders newOrder={newOrder} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="deliveryOrders/:id"
                            element={
                                <ProtectedRoute>
                                    <DeliveryOrderDetails showMessage={showMessage} />
                                </ProtectedRoute>
                            }
                        />
                        {/* كشوف الحسابات — DEPRECATED, read-only archive. The
                        client reverted to the old bulk-delete billing flow
                        (2026-07); these routes stay only so existing
                        statements (incl. the unpaid ledger) remain viewable
                        until the data is settled/removed. Password gate
                        (ElevationGate) intentionally kept. */}
                        <Route
                            path="statements"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_orders ||
                                        adminInfo?.role === "vendor"
                                    }
                                >
                                    <ElevationGate>
                                        <Statements />
                                    </ElevationGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="statements/:id"
                            element={
                                <ProtectedRoute
                                    condition={
                                        adminInfo?.super_admin ||
                                        adminInfo?.roles?.manage_orders ||
                                        adminInfo?.role === "vendor"
                                    }
                                >
                                    <ElevationGate>
                                        <StatementDetail />
                                    </ElevationGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="reviews"
                            element={
                                <ProtectedRoute condition={canManageReviews(adminInfo)}>
                                    <Reviews />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="reviews/:id"
                            element={
                                <ProtectedRoute condition={canManageReviews(adminInfo)}>
                                    <ReviewDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="my-reviews"
                            element={
                                <ProtectedRoute condition={adminInfo?.role === "vendor"}>
                                    <MyVendorReviews />
                                </ProtectedRoute>
                            }
                        />
                        {/* The restaurant's own wallet, read-only (admin#41).
                            Admins open a restaurant's wallet from the
                            restaurants table instead. */}
                        <Route
                            path="wallet"
                            element={
                                <ProtectedRoute condition={adminInfo?.role === "vendor"}>
                                    <MyVendorWallet />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="options"
                            element={
                                <ProtectedRoute>
                                    <Options />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="groups"
                            element={
                                <ProtectedRoute>
                                    <Groups />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="sliders"
                            element={
                                <ProtectedRoute>
                                    <Sliders />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="banners"
                            element={
                                <ProtectedRoute>
                                    <Banners />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="regions"
                            element={
                                <ProtectedRoute>
                                    <Regions />
                                </ProtectedRoute>
                            }
                        />
                        {/*
                            ارسال اشعار used to be the first card on the settings
                            page. It is a marketing broadcast on a campaign
                            cadence, not a setting, and it was unfindable filed
                            at the bottom of the nav — so it has its own route
                            under التسويق now. Ungated, matching the settings
                            page it came from.
                        */}
                        <Route
                            path="send-notification"
                            element={
                                <ProtectedRoute>
                                    <SendNotification />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="send-notification/:id"
                            element={
                                <ProtectedRoute>
                                    <NotificationCampaignDetails />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="messaging-health"
                            element={
                                <ProtectedRoute condition={adminInfo?.role === "admin"}>
                                    <MessagingHealth />
                                </ProtectedRoute>
                            }
                        />
                        {/*
                            NOT super-admin-gated, despite holding AppVersions. The
                            page is four unrelated tools: a push broadcast, the
                            التنبيه banner, the الاغلاق kill switch, and app versions.
                            The first three are ordinary operational controls an admin
                            needs — one of them is how you stop ordering in a hurry —
                            so gating the route locked them out to protect the fourth.
                            The force-update lever is gated inside the page instead,
                            and enforced for real by requireSuperAdmin on
                            PUT /api/app-versions/:app.
                        */}
                        <Route
                            path="settings"
                            element={
                                <ProtectedRoute>
                                    <Setting />
                                </ProtectedRoute>
                            }
                        />
                    </Route>
                </Routes>
            </ErrorBoundary>
        </>
    );
}

export default App;
